import type {
  CreateFeature,
  FeatureQuery,
  FeatureResponse,
  UpdateFeature,
} from '@monorepo/shared/schemas/features'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { featureService } from '@/features/features/services/featureService'
import { toast } from '@/shared/lib/toast'

// Legacy aliases for backwards compatibility
type Feature = FeatureResponse
type CreateFeatureRequest = CreateFeature
type UpdateFeatureRequest = UpdateFeature
type FeatureFilters = FeatureQuery

// ============================================================================
// QUERY KEY FACTORY
// ============================================================================
// Centralized query keys for consistent cache management
export const featureKeys = {
  all: ['features'] as const,
  lists: () => [...featureKeys.all, 'list'] as const,
  list: (filters?: FeatureFilters) =>
    [...featureKeys.lists(), filters] as const,
  details: () => [...featureKeys.all, 'detail'] as const,
  detail: (id: string) => [...featureKeys.details(), id] as const,
}

export function useFeatures(initialFilters?: FeatureFilters) {
  const [features, setFeatures] = useState<Feature[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<FeatureFilters>(initialFilters || {})

  const fetchFeatures = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await featureService.getFeatures(filters)
      setFeatures(response.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch features')
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    fetchFeatures()
  }, [fetchFeatures])

  const createFeature = useCallback(
    async (data: CreateFeatureRequest): Promise<Feature> => {
      try {
        const feature = await featureService.createFeature(data)
        setFeatures(prev => [feature, ...prev])
        return feature
      } catch (err) {
        const error =
          err instanceof Error ? err.message : 'Failed to create feature'
        setError(error)
        throw new Error(error)
      }
    },
    []
  )

  const updateFeature = useCallback(
    async (id: string, data: UpdateFeatureRequest): Promise<Feature> => {
      try {
        const feature = await featureService.updateFeature(id, data)
        setFeatures(prev => prev.map(f => (f.id === id ? feature : f)))
        return feature
      } catch (err) {
        const error =
          err instanceof Error ? err.message : 'Failed to update feature'
        setError(error)
        throw new Error(error)
      }
    },
    []
  )

  const deleteFeature = useCallback(async (id: string): Promise<void> => {
    try {
      await featureService.deleteFeature(id)
      setFeatures(prev => prev.filter(feature => feature.id !== id))
    } catch (err) {
      const error =
        err instanceof Error ? err.message : 'Failed to delete feature'
      setError(error)
      throw new Error(error)
    }
  }, [])

  const updateFilters = useCallback((newFilters: Partial<FeatureFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }))
  }, [])

  const clearFilters = useCallback(() => {
    setFilters({})
  }, [])

  const refresh = useCallback(() => {
    fetchFeatures()
  }, [fetchFeatures])

  return {
    features,
    loading,
    error,
    filters,
    createFeature,
    updateFeature,
    deleteFeature,
    updateFilters,
    clearFilters,
    refresh,
  }
}

export function useFeature(id: string) {
  const [feature, setFeature] = useState<Feature | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchFeature = useCallback(async () => {
    if (!id) return

    try {
      setLoading(true)
      setError(null)
      const feature = await featureService.getFeature(id)
      setFeature(feature)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch feature')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    fetchFeature()
  }, [fetchFeature])

  const updateFeature = useCallback(
    async (data: UpdateFeatureRequest): Promise<Feature> => {
      if (!feature) throw new Error('No feature loaded')

      try {
        const updatedFeature = await featureService.updateFeature(
          feature.id,
          data
        )
        setFeature(updatedFeature)
        return updatedFeature
      } catch (err) {
        const error =
          err instanceof Error ? err.message : 'Failed to update feature'
        setError(error)
        throw new Error(error)
      }
    },
    [feature]
  )

  const refresh = useCallback(() => {
    fetchFeature()
  }, [fetchFeature])

  return {
    feature,
    loading,
    error,
    updateFeature,
    refresh,
  }
}

// React Query versions for better caching and state management
export function useFeatureQuery(id: string) {
  return useQuery({
    queryKey: featureKeys.detail(id),
    queryFn: () => featureService.getFeature(id),
    enabled: !!id,
  })
}

export function useCreateFeature() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateFeatureRequest) =>
      featureService.createFeature(data),
    onSuccess: newFeature => {
      // Invalidate all list queries (regardless of filters)
      queryClient.invalidateQueries({ queryKey: featureKeys.lists() })
      toast.success(
        `Feature "${newFeature.feature_name}" has been created successfully.`
      )
    },
    onError: (error: unknown) => {
      // Extract error message from API response
      const err = error as {
        response?: { data?: { error?: { message?: string }; message?: string } }
        message?: string
      }
      const errorMessage =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to create feature'
      toast.error(errorMessage)
    },
  })
}

export function useUpdateFeature() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, ...data }: UpdateFeatureRequest & { id: string }) =>
      featureService.updateFeature(id, data),
    onSuccess: (updatedFeature, variables) => {
      // Invalidate all list queries (regardless of filters)
      queryClient.invalidateQueries({ queryKey: featureKeys.lists() })
      // Invalidate specific feature detail
      queryClient.invalidateQueries({
        queryKey: featureKeys.detail(variables.id),
      })
      toast.success(
        `Feature "${updatedFeature.feature_name}" has been updated successfully.`
      )
    },
    onError: (error: unknown) => {
      // Extract error message from API response
      const err = error as {
        response?: { data?: { error?: { message?: string }; message?: string } }
        message?: string
      }
      const errorMessage =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to update feature'
      toast.error(errorMessage)
    },
  })
}

export function useDeleteFeature() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => featureService.deleteFeature(id),
    onSuccess: () => {
      // Invalidate all list queries (regardless of filters)
      queryClient.invalidateQueries({ queryKey: featureKeys.lists() })
      toast.success('Feature has been deleted successfully.')
    },
    onError: (error: unknown) => {
      // Extract error message from API response
      const err = error as {
        response?: { data?: { error?: { message?: string }; message?: string } }
        message?: string
      }
      const errorMessage =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to delete feature'
      toast.error(errorMessage)
    },
  })
}
